import { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Textarea,
} from '@roger/ui-components';
import {
  getEpisodeWorksheet,
  updateEpisodeWorksheet,
} from '../../services/api';
import type {
  CurrentConditionIndicator,
  EpisodeWorksheetRead,
  EpisodeWorksheetUpdate,
  UsState,
} from '../../services/api/types.gen';
import type { PrimaryDiagnosisFitness } from '../../services/api/types.gen';
import { USSTATE_VALUES } from '../../services/api/models/UsState';
import { US_STATES } from '../../constants/options';
import PrimaryFitnessConfirmDialog from '../PrimaryFitnessConfirmDialog';
import { usePrimaryDiagnosisFitness } from '../../hooks/usePrimaryDiagnosisFitness';
import {
  fitnessPresentation,
  shouldDiscourage,
} from '../../utils/primaryDiagnosisFitness';
import { useToast } from '../../hooks/useToastContext';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';
import { isValidDollarAmount } from '../../utils/currencyUtils';
import { DateField, DropdownField, Field } from '../FormFields';
import { ConditionCodesField } from '../billing/worksheet/ConditionCodesField';
import { EditSection } from '../billing/worksheet/EditSection';
import { OccurrenceCodesField } from '../billing/worksheet/OccurrenceCodesField';
import { OccurrenceSpansField } from '../billing/worksheet/OccurrenceSpansField';
import { ValueCodesField } from '../billing/worksheet/ValueCodesField';
import InlineDiagnosisSearch from '../oasis/InlineDiagnosisSearch';

const STATE_LABEL: Record<string, string> = Object.fromEntries(
  US_STATES.map(s => [s.value, s.label])
);
// Leading empty option lets the user clear the field back to null under full-replace PUT.
const stateOptions = [
  { value: '', label: '—' },
  ...USSTATE_VALUES.map(v => ({
    value: v,
    label: `${v} — ${STATE_LABEL[v] ?? v}`,
  })),
];
export function EpisodeWorksheetEditor({
  episodeId,
  onClose,
}: {
  episodeId: string;
  onClose: () => void;
}) {
  const { success: showSuccess, error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // One piece of edit state mirroring EpisodeWorksheetUpdate.
  const [employer, setEmployer] = useState('');
  const [employment, setEmployment] = useState(false);
  const [autoAccident, setAutoAccident] = useState(false);
  const [otherAccident, setOtherAccident] = useState(false);
  const [accidentState, setAccidentState] = useState<UsState | ''>('');
  const [accidentDate, setAccidentDate] = useState<string | null>(null);
  // Values not currently shown in the UI, but preserved on full-replace PUT so
  // saving visible worksheet edits does not silently erase existing data.
  const [illnessDate, setIllnessDate] = useState<string | null>(null);
  const [conditionIndicator, setConditionIndicator] = useState<
    CurrentConditionIndicator | ''
  >('');
  const [hospFrom, setHospFrom] = useState<string | null>(null);
  const [hospTo, setHospTo] = useState<string | null>(null);
  const [outsideLab, setOutsideLab] = useState('');
  // Codes (Task 9).
  const [diagnosisCodes, setDiagnosisCodes] = useState<string[]>([]);
  const [pendingPrincipal, setPendingPrincipal] = useState<{
    code: string;
    fitness: PrimaryDiagnosisFitness | null;
  } | null>(null);
  // Only the principal needs judging, but ask about it by the same route the
  // rest of the app uses so the answer cannot diverge per surface.
  const principalCode = diagnosisCodes[0];
  const { fitnessByCode, pdgmRelevant } = usePrimaryDiagnosisFitness(
    principalCode ? [principalCode] : [],
    { episodeId }
  );
  const principalWarning = principalCode
    ? fitnessPresentation(fitnessByCode[principalCode], {
        softened: !pdgmRelevant,
      })
    : null;
  const [conditionCodes, setConditionCodes] = useState<string[]>([]);
  const [occurrenceCodes, setOccurrenceCodes] = useState<
    { code: string; date: string }[]
  >([]);
  const [valueCodes, setValueCodes] = useState<
    { code: string; amount: string }[]
  >([]);
  const [occurrenceSpans, setOccurrenceSpans] = useState<
    { code: string; from_date: string; through_date: string }[]
  >([]);
  const [remarks, setRemarks] = useState('');

  const seed = (d: EpisodeWorksheetRead) => {
    setEmployer(d.employer ?? '');
    setEmployment(d.condition_related_to_employment);
    setAutoAccident(d.condition_related_to_auto_accident);
    setOtherAccident(d.condition_related_to_other_accident);
    setAccidentState((d.auto_accident_state as UsState) ?? '');
    setAccidentDate(d.accident_date ?? null);
    setIllnessDate(d.date_of_current_illness_or_injury ?? null);
    setConditionIndicator(
      (d.current_condition_indicator as CurrentConditionIndicator) ?? ''
    );
    setHospFrom(d.hospitalization_from_date ?? null);
    setHospTo(d.hospitalization_to_date ?? null);
    setOutsideLab(
      d.outside_lab_charge != null ? String(d.outside_lab_charge) : ''
    );
    setDiagnosisCodes(d.diagnosis_codes ?? []);
    setConditionCodes(d.condition_codes ?? []);
    setOccurrenceCodes(
      (d.occurrence_codes ?? []).map(r => ({ code: r.code, date: r.date }))
    );
    setValueCodes(
      (d.value_codes ?? []).map(r => ({ code: r.code, amount: r.amount }))
    );
    setOccurrenceSpans(
      (d.occurrence_spans ?? []).map(r => ({
        code: r.code,
        from_date: r.from_date,
        through_date: r.through_date,
      }))
    );
    setRemarks(d.remarks ?? '');
  };

  useEffect(() => {
    let active = true;
    void (async () => {
      setLoading(true);
      try {
        const resp = await getEpisodeWorksheet({
          path: { episode_id: episodeId },
        });
        if (!active) return;
        if (resp.error || !resp.data) {
          setLoadError(true);
          return;
        }
        seed(resp.data);
      } catch (e) {
        if (active) setLoadError(true);
        console.error('Failed to load episode worksheet', e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [episodeId]);

  const anyFlag = employment || autoAccident || otherAccident;

  // Mirror ClaimWorksheetEditor: BLOCK on partial rows (one field filled, the other blank)
  // and on a span whose through_date precedes its from_date. Fully-blank rows are dropped (not blocked).
  const validate = (): string | null => {
    if (anyFlag && !employer.trim() && !accidentState)
      return "When a claim is marked as workers' comp or accident-related, you must provide an employer or an accident state.";
    const partialOcc = occurrenceCodes.some(
      r => Boolean(r.code) !== Boolean(r.date)
    );
    const partialVal = valueCodes.some(
      r => Boolean(r.code) !== Boolean(r.amount)
    );
    const partialSpan = occurrenceSpans.some(
      r =>
        [r.code, r.from_date, r.through_date].some(Boolean) &&
        ![r.code, r.from_date, r.through_date].every(Boolean)
    );
    if (partialOcc || partialVal || partialSpan)
      return 'Complete or remove code rows missing a date or amount.';
    const badSpanOrder = occurrenceSpans.some(
      r => r.from_date && r.through_date && r.through_date < r.from_date
    );
    if (badSpanOrder)
      return 'Occurrence span end date must be on or after the start date.';

    if (
      valueCodes.some(
        v => v.amount.trim() !== '' && !isValidDollarAmount(v.amount.trim())
      )
    )
      return 'Enter a valid dollar amount for each value code.';
    return null;
  };

  // After validate() passes, only fully-complete rows remain meaningful; drop fully-blank rows.
  const buildBody = (): EpisodeWorksheetUpdate => ({
    employer: employer.trim() || null,
    accident_date: accidentDate || null,
    condition_related_to_employment: employment,
    condition_related_to_auto_accident: autoAccident,
    condition_related_to_other_accident: otherAccident,
    auto_accident_state: accidentState || null,
    date_of_current_illness_or_injury: illnessDate || null,
    current_condition_indicator: conditionIndicator || null,
    hospitalization_from_date: hospFrom || null,
    hospitalization_to_date: hospTo || null,
    outside_lab_charge: outsideLab.trim() === '' ? null : outsideLab.trim(),
    diagnosis_codes: diagnosisCodes,
    condition_codes: conditionCodes.map(c => c.trim()).filter(Boolean),
    occurrence_codes: occurrenceCodes.filter(r => r.code && r.date),
    occurrence_spans: occurrenceSpans.filter(
      r => r.code && r.from_date && r.through_date
    ),
    value_codes: valueCodes.filter(r => r.code && r.amount),
    remarks: remarks.trim() || null,
  });

  const handleSave = async () => {
    const v = validate();
    if (v) {
      setValidationError(v);
      return;
    }
    setValidationError(null);
    setSaving(true);
    try {
      const resp = await updateEpisodeWorksheet({
        path: { episode_id: episodeId },
        body: buildBody(),
      });
      if (resp.error || !resp.data) throw resp;
      seed(resp.data);
      showSuccess('Worksheet saved', 'Episode worksheet was updated.');
      onClose();
    } catch (e) {
      console.error('Failed to save episode worksheet', e);
      showError(
        'Save failed',
        getUserFacingApiMessage(
          e,
          'Unable to save the worksheet. Please try again.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={o => {
        if (!o) onClose();
      }}
    >
      <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-5xl h-[85vh] max-h-[85vh] overflow-hidden flex flex-col gap-0 p-0'>
        <DialogHeader className='border-b bg-gradient-to-r from-slate-50 to-white px-6 py-4'>
          <DialogTitle>Episode Worksheet</DialogTitle>
          <DialogDescription className='sr-only'>
            Editable UB-04 claim worksheet fields for the selected episode.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className='min-h-0 flex-1 py-8 text-center text-sm text-muted-foreground'>
            Loading…
          </p>
        ) : loadError ? (
          <div className='min-h-0 flex-1 overflow-y-auto px-6 py-4'>
            <div className='rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700'>
              Unable to load the worksheet. Please try again.
            </div>
          </div>
        ) : (
          <main
            data-testid='episode-worksheet-scroll-region'
            className='min-h-0 flex-1 overflow-y-auto bg-slate-50/60 px-6 py-5'
          >
            <fieldset disabled={saving} className='space-y-5'>
              <EditSection title='Condition / Accident'>
                <div className='grid grid-cols-1 gap-x-4 gap-y-3 lg:grid-cols-[minmax(15rem,1fr)_minmax(10rem,12rem)_minmax(10rem,12rem)_minmax(14rem,1.25fr)]'>
                  {/* Single accessible name per checkbox: id+htmlFor label (no aria-label → avoids double-labeling). */}
                  <div className='space-y-2 rounded-lg border border-slate-200 bg-white/70 p-3'>
                    <div className='flex items-center gap-2'>
                      <Checkbox
                        id='ew-employment'
                        checkboxSize='sm'
                        checked={employment}
                        onCheckedChange={c => setEmployment(Boolean(c))}
                      />
                      <label htmlFor='ew-employment' className='text-sm'>
                        Related to employment (FL 10a)
                      </label>
                    </div>
                    <div className='flex items-center gap-2'>
                      <Checkbox
                        id='ew-auto-accident'
                        checkboxSize='sm'
                        checked={autoAccident}
                        onCheckedChange={c => setAutoAccident(Boolean(c))}
                      />
                      <label htmlFor='ew-auto-accident' className='text-sm'>
                        Related to auto accident (FL 10b)
                      </label>
                    </div>
                    <div className='flex items-center gap-2'>
                      <Checkbox
                        id='ew-other-accident'
                        checkboxSize='sm'
                        checked={otherAccident}
                        onCheckedChange={c => setOtherAccident(Boolean(c))}
                      />
                      <label htmlFor='ew-other-accident' className='text-sm'>
                        Related to other accident (FL 10c)
                      </label>
                    </div>
                  </div>
                  <div className='max-w-48'>
                    <DropdownField
                      label='Accident state (FL 29)'
                      htmlFor='ew-accident-state'
                      value={accidentState}
                      options={stateOptions}
                      onChange={v => setAccidentState(v as UsState | '')}
                      fieldSize='sm'
                    />
                  </div>
                  <div className='max-w-48'>
                    <DateField
                      label='Accident date (FL 31)'
                      htmlFor='ew-accident-date'
                      value={accidentDate}
                      onChange={setAccidentDate}
                      fieldSize='sm'
                      typable
                    />
                  </div>
                  <Field
                    label='Employer'
                    htmlFor='ew-employer'
                    value={employer}
                    onChange={setEmployer}
                    fieldSize='sm'
                  />
                </div>
              </EditSection>
              <EditSection title='Diagnoses'>
                {principalWarning && (
                  <p
                    className={`mb-2 text-xs leading-snug ${principalWarning.toneClass}`}
                    data-testid='worksheet-principal-fitness-warning'
                  >
                    {principalCode} is the principal diagnosis (FL 67).{' '}
                    {principalWarning.message}
                  </p>
                )}
                <div className='flex flex-wrap gap-2'>
                  {diagnosisCodes.map(code => (
                    <span
                      key={code}
                      className='inline-flex items-center gap-1 rounded bg-muted px-2 py-1 font-mono text-sm'
                    >
                      {code}
                      <button
                        type='button'
                        aria-label={`Remove ${code}`}
                        onClick={() =>
                          setDiagnosisCodes(
                            diagnosisCodes.filter(c => c !== code)
                          )
                        }
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <InlineDiagnosisSearch
                  placeholder='Search diagnosis code…'
                  excludeCodes={diagnosisCodes}
                  // The first code on the worksheet is the claim's principal
                  // diagnosis (FL 67), so only an empty list seats one here.
                  primaryContext={diagnosisCodes.length === 0}
                  pdgmRelevant={pdgmRelevant}
                  onSelect={(code, _desc, fitness) => {
                    if (diagnosisCodes.includes(code)) return;
                    const seatsPrincipal = diagnosisCodes.length === 0;
                    if (
                      seatsPrincipal &&
                      pdgmRelevant &&
                      shouldDiscourage(fitness)
                    ) {
                      setPendingPrincipal({ code, fitness: fitness ?? null });
                      return;
                    }
                    setDiagnosisCodes([...diagnosisCodes, code]);
                  }}
                />
              </EditSection>
              <EditSection title='Condition Codes (FL 18–28)'>
                <ConditionCodesField
                  values={conditionCodes}
                  onChange={setConditionCodes}
                />
              </EditSection>
              <EditSection title='Occurrence Codes (FL 31–34)'>
                <OccurrenceCodesField
                  rows={occurrenceCodes}
                  onChange={setOccurrenceCodes}
                />
              </EditSection>
              <EditSection title='Value Codes (FL 39–41)'>
                <ValueCodesField rows={valueCodes} onChange={setValueCodes} />
              </EditSection>
              <EditSection title='Occurrence Spans (FL 35–36)'>
                <OccurrenceSpansField
                  rows={occurrenceSpans}
                  onChange={setOccurrenceSpans}
                />
              </EditSection>
              <EditSection title='Remarks (FL 80)'>
                <Textarea
                  textareaSize='sm'
                  value={remarks}
                  onChange={e => setRemarks(e.target.value)}
                />
              </EditSection>
            </fieldset>
          </main>
        )}
        <div className='flex items-center justify-end gap-2 border-t px-6 py-4'>
          {validationError && (
            <span className='mr-auto text-sm text-red-600'>
              {validationError}
            </span>
          )}
          <Button variant='outline' onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={saving || loading || loadError}
          >
            {saving ? 'Saving…' : 'Save & Close'}
          </Button>
        </div>
      </DialogContent>
      <PrimaryFitnessConfirmDialog
        code={pendingPrincipal?.code ?? null}
        fitness={pendingPrincipal?.fitness ?? null}
        onConfirm={() => {
          if (pendingPrincipal) {
            setDiagnosisCodes([...diagnosisCodes, pendingPrincipal.code]);
          }
          setPendingPrincipal(null);
        }}
        onCancel={() => setPendingPrincipal(null)}
      />
    </Dialog>
  );
}
